"use client"

import logger from "@/lib/logger/logger-init"
import { type RequiredTokenAndSelectionMetadata } from "@/types/cookie-types"
import { type Result, ResultFactory } from "@/types/generic/Result"
import { type ChangeShippingDestinationOrLanguageResult } from "./types"

export interface ShippingDestinationFormData extends FormData {
    append(name: "countryCode", value: string | Blob, fileName?: string): void
    append(name: "countryName", value: string | Blob, fileName?: string): void
}

export const changeShippingDestination = async (
    formData: ShippingDestinationFormData
): Promise<Result<ChangeShippingDestinationOrLanguageResult>> => {
    const countryCode = formData.get("countryCode")
    const countryName = formData.get("countryName")

    if (!countryCode || typeof countryCode !== "string") {
        const errorMessage = "Country code is required"
        logger.error(errorMessage)
        return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
            new Error(errorMessage)
        )
    }

    if (!countryName || typeof countryName !== "string") {
        const errorMessage = "Country name is required"
        logger.error(errorMessage)
        return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
            new Error(errorMessage)
        )
    }

    try {
        const response = await fetch("/api/centra/change-shipping-destination", {
            method: "POST",
            credentials: "include",
            body: formData
        })

        const result = await response.json()

        if (!response.ok || !result.success) {
            const errorMessage = result.error || "Failed to change shipping destination"
            logger.error(errorMessage)
            return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
                new Error(errorMessage)
            )
        }

        const { checkoutApiSelection, basketSessionCookie } = result.data

        // The route handler already sets the basket_session cookie in the HTTP response
        // Set-Cookie header — no client-side re-write needed.
        return ResultFactory.success<ChangeShippingDestinationOrLanguageResult>({
            checkoutApiSelection: checkoutApiSelection,
            basketSessionCookie: basketSessionCookie as RequiredTokenAndSelectionMetadata
        })
    } catch (error) {
        logger.error("Error in changeShippingDestination client action", error)
        return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
            error instanceof Error
                ? error
                : new Error("Failed to change shipping destination")
        )
    }
}
